import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import {
  Divider,
  Select,
  Option,
  RadioGroup,
  RadioButtonGroup,
  RadioButton,
  TextField,
  TextArea,
  Link,
  useToast,
  Icon,
} from '@workramp/ui';
import chroma from 'chroma-js';
import { ColorPickerSetting, MenuSetting } from '~/clc/features/theming/admin/components';
import {
  CUSTOM_FONT,
  CUSTOM_GOOGLE_FONT_FAMILY,
  WrFontPicker,
  DefaultLandingPageSelect,
} from '~/components/academies/admin/theming';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { TextEditor } from '~/features/rich-text-editor';
import { WRModals } from '~/components/wr-modals/wr-modals-container';
import { theme } from '~/styles';
import ImageSettingEditor from '~/components/enterprise-setup/enterprise_settings_editor/image_setting_editor';
import AcademyAdminiPhoneChrome from '~/components/academies/common/academy-admin-iphone-chrome';
import AcademyAdminiPhoneBottom from '~/components/academies/common/academy-admin-iphone-bottom';
import AcademyAdminiPhoneTop from '~/components/academies/common/academy-admin-iphone-top';
import AcademiesApi from '~/agent/academies';
import { ObjectUtils } from '~/common';
import Toggle from '~/components/widget/toggle';
import ReactTooltipInfoMarker from '~/components/widget/react-tooltip-info-marker';
import {
  AnnouncementBannerSectionEditorSettings,
  AnnouncementBannerSettingsTabContent,
} from '../AnnouncementBannerSettingsTabContent';

const ADMIN_HEIGHT = 128;
const MOBILE_HEIGHT = 848;
const MOBILE_WIDTH = 417;
const MOBILE_CONTENT_HEIGHT = 812;
const MOBILE_CONTENT_WIDTH = 375;
const NUMBER_OF_LANDING_SIDEBAR_GROUPS = isFeatureEnabled('fy26-q2-announcement-banner') ? 5 : 4;
const NUMBER_OF_REGISTRATION_SIDEBAR_GROUPS = 1;
const NUMBER_OF_COURSE_OVERVIEW_SIDEBAR_GROUPS = 1;
const SIDEBAR_TOGGLE_HEIGHT = 48;
const SIDEBAR_WIDTH = 350;
const THEME_TOGGLE_HEIGHT = 42;

class BaseAcademyAdminTheming extends Component {
  constructor(props) {
    super(props);
    const { academy } = this.props;

    this.state = {
      academy,
      previewDisplay: 'desktop',
      previewExpanded: false,
      previewKey: 0,
      displayedColorPicker: null,
      theming: 'landing',
      customFont: academy?.themeOptions?.customFont,
      visibleSidebarGroup: 'header',
      termsAndConditions: academy.themeOptions.termsAndConditions,
      loginTermsAndConditions: academy.themeOptions.loginTermsAndConditions,
    };
  }

  availableCustomizations = {
    courseOverview: {
      body: {
        accentColorPicker: true,
        breadCrumbLinkColorPicker: true,
      },
    },
    home: {
      header: {
        logoUploader: true,
        logoHeightEditor: true,
        faviconUploader: true,
        titleEditor: true,
        descriptionEditor: true,
        backgroundImageUploader: true,
        darkenBackgroundButtonGroup: true,
        foregroundImageUploader: true,
        imageAlignButtonGroup: true,
        titleColorPicker: true,
        descriptionColorPicker: true,
        textAlignButtonGroup: true,
        subnavAlignButtonGroup: true,
        headerLinksEditor: true,
        showSearchToggleButtonGroup: true,
        showFiltersToggleButtonGroup: true,
        backToLinkToggleButtonGroup: true,
        backToLinkEditor: true,
        backToLinkTextEditor: true,

        subnavBackgroundColorPicker: true,
        subnavLinkColorPicker: true,
        subnavActiveLinkColorPicker: true,

        trainingsTabTitleTextEditor: true,
        pathsTabTitleTextEditor: true,
        certificationsTabTitleTextEditor: true,
        eventsTabTitleTextEditor: true,

        // the following customizations haven't been properly ordered in the UI yet, and are disabled anyways
        homePageLinkEditor: false,
        linkColorPicker: false,
        linkHoverColorPicker: false,
        imageStyleButtonGroup: false,
      },
      body: {
        googleFontPicker: true,
        numberOfColumnsButtonGroup: false,
        cardStyleButtonGroup: false,
        collectionCardVersionButtonGroup: true,
        defaultLandingTabDropdown: true,
        contentLabelOverridesEditor: true,
      },
      footer: {
        footerBackgroundColorPicker: true,
        footerTextEditor: true,
        footerTextColorPicker: true,
        footerLinksEditor: true,
        showFooterCredits: true,
        hideFooterLanguagePickerButtonGroup: true,
      },
    },
    registration: {
      body: {
        registrationBodyBackgroundImageUploader: true,
        registrationBodyLogoUploader: true,
        termsAndConditionsTextEditor: true,
        loginRegisterButtonBackgroundColorPicker: true,
        loginRegisterButtonHoverBackgroundColorPicker: false,
        loginRegisterButtonTextColorPicker: false,
        loginRegisterLinkColorPicker: true,
      },
    },
  };

  stopPropagation = (e) => {
    e.stopPropagation();
  };

  render() {
    if (this.props.msg) return this.renderBlankPage();

    return (
      <PageWrapper className="t18-page-wrapper no-sidebar">
        {this.renderSidebar()}
        <ThemingRight previewExpanded={this.state.previewExpanded}>
          {this.renderThemingBar()}
          {this.renderPreview()}
        </ThemingRight>
      </PageWrapper>
    );
  }

  UNSAFE_componentWillMount() {
    this.debouncedIncrementPreviewKey = _.debounce(this.incrementPreviewKey, 1000);
  }

  componentDidUpdate(_prevProps, prevState) {
    const stateFieldsToDebounce = [
      'homepageLink',
      'backToLink',
      'backToLinkText',
      'title',
      'description',
      'footerText',
      'termsAndConditions',
      'loginTermsAndConditions',
      'requireExplicitTermsAndConditionsAgreement',
      'contentLabelOverrides',
    ];

    const stateFieldsToNotUpdatePreview = [
      'previewDisplay',
      'previewExpanded',
      'previewKey',
      'visibleSidebarGroup',
      'displayedColorPicker',
    ];

    const changedObj = ObjectUtils.diff(prevState, this.state);
    const changedFields = ObjectUtils.keysAndNestedKeys(changedObj);

    if (!_.isEmpty(_.intersection(changedFields, stateFieldsToDebounce))) {
      this.debouncedIncrementPreviewKey();
    } else if (!_.isEmpty(_.intersection(changedFields, stateFieldsToNotUpdatePreview))) {
    } else if (!_.isEmpty(changedFields)) {
      this.incrementPreviewKey();
    }
  }

  switchTheming = (e, theming, visibleSidebarGroup) => {
    e.preventDefault();
    this.setState({
      theming,
      visibleSidebarGroup,
    });
  };

  renderBlankPage = () => <div className="mtxl text-center grey-5 fsm">{this.props.msg}</div>;

  renderThemingBar = () => (
    <AcademyThemeingChoices>
      {this.renderSidebarUtilities()}
      <a
        href="#"
        className={this.state.theming === 'landing' ? 'active' : ''}
        onClick={(e) => this.switchTheming(e, 'landing', 'header')}
      >
        {this.state.academy.themeOptions.contentLabelOverrides[APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.TRAININGS.KEY] ||
          'Trainings'}{' '}
        Page
      </a>
      <a
        href="#"
        className={this.state.theming === 'register' ? 'active' : ''}
        onClick={(e) => this.switchTheming(e, 'register', 'registration-body')}
      >
        Registration Page
      </a>
      <a
        href="#"
        className={this.state.theming === 'courseOverview' ? 'active' : ''}
        onClick={(e) => this.switchTheming(e, 'courseOverview', 'course-overview-body')}
      >
        Content Overview Pages
      </a>
    </AcademyThemeingChoices>
  );

  renderSidebar = () => {
    let sidebarContent = '';
    switch (this.state.theming) {
      case 'landing':
        sidebarContent = this.renderLandingSidebar();
        break;
      case 'register':
        sidebarContent = this.renderRegistrationSidebar();
        break;
      case 'courseOverview':
        sidebarContent = this.renderCourseOverviewSidebar();
        break;
      default:
        break;
    }

    return (
      <AcademyThemingSidebar previewExpanded={this.state.previewExpanded}>
        <div className="settings-editor-outer">
          <div className="settings-editor grey-6">{sidebarContent}</div>
        </div>
      </AcademyThemingSidebar>
    );
  };

  renderLandingSidebar = () => (
    <>
      {isFeatureEnabled('fy26-q2-announcement-banner') && this.renderAnnouncementBannerGroup()}
      {this.renderSidebarHeaderGroup()}
      {this.renderSidebarBodyGroup()}
      {this.renderSidebarFooterGroup()}
      {this.renderSidebarCustomCode()}
    </>
  );

  renderRegistrationSidebar = () => <>{this.renderRegistrationSidebarBodyGroup()}</>;

  renderCourseOverviewSidebar = () => <>{this.renderCourseOverviewSidebarBodyGroup()}</>;

  renderSidebarUtilities = () => (
    <SidebarUtilitiesWrapper previewExpanded={this.state.previewExpanded}>
      <ButtonGroup>
        <Button
          onClick={() => this.setState({ previewExpanded: !this.state.previewExpanded })}
          hoverBackgroundColor={theme.color.darkBlue}
          buttonWidth={100 / 3}
          className="active"
        >
          <i className="fas fa-fw fa-caret-left" />
          <i className="fas fa-fw fa-caret-right" />
        </Button>
        <Button
          onClick={() => this.setState({ previewDisplay: 'desktop' })}
          active={this.state.previewDisplay === 'desktop'}
          hoverBackgroundColor={theme.color.darkBlue}
          buttonWidth={100 / 3}
          className={this.state.previewDisplay === 'desktop' && 'active'}
        >
          <i className="fas fa-fw fa-desktop" />
        </Button>
        <Button
          onClick={() => this.setState({ previewDisplay: 'mobile' })}
          active={this.state.previewDisplay === 'mobile'}
          hoverBackgroundColor={theme.color.darkBlue}
          buttonWidth={100 / 3}
          className={this.state.previewDisplay === 'mobile' && 'active'}
        >
          <i className="fas fa-fw fa-mobile" />
        </Button>
      </ButtonGroup>
    </SidebarUtilitiesWrapper>
  );

  showSidebarGroup = (group) => {
    if (this.state.visibleSidebarGroup === group) group = '';

    this.setState({
      visibleSidebarGroup: group,
    });
  };

  isCustomizationEnabled = (page, section, customization) => {
    if (
      this.availableCustomizations[page] &&
      this.availableCustomizations[page][section] &&
      this.availableCustomizations[page][section][customization]
    ) {
      return true;
    }

    return false;
  };

  renderAnnouncementBannerGroup = () => {
    const { key, iconName, name } = AnnouncementBannerSectionEditorSettings;
    const GROUP_KEY = key;

    return (
      <AcademyThemingSidebarGroup>
        <AcademySidebarGroupToggle
          onClick={() => this.showSidebarGroup(GROUP_KEY)}
          visible={this.state.visibleSidebarGroup === GROUP_KEY}
        >
          <Icon name={iconName} size="lg" />
          {name}
        </AcademySidebarGroupToggle>

        <SidebarExpandable
          visible={this.state.visibleSidebarGroup === GROUP_KEY}
          numberOfSidebarGroups={this.numberOfSidebarGroups()}
        >
          <AnnouncementBannerSettingsTabContent
            academyId={this.props.academy.id}
            visible={this.state.visibleSidebarGroup === GROUP_KEY}
            onChange={() => this.incrementPreviewKey()}
          />
        </SidebarExpandable>
      </AcademyThemingSidebarGroup>
    );
  };

  renderSidebarHeaderGroup = () => (
    <AcademyThemingSidebarGroup>
      <AcademySidebarGroupToggle
        onClick={() => this.showSidebarGroup('header')}
        visible={this.state.visibleSidebarGroup === 'header'}
      >
        <Icon name="layout-header:regular" size="lg" />
        Header
      </AcademySidebarGroupToggle>

      <SidebarExpandable
        visible={this.state.visibleSidebarGroup === 'header'}
        numberOfSidebarGroups={this.numberOfSidebarGroups()}
      >
        {this.isCustomizationEnabled('home', 'header', 'logoUploader') && this.renderLogoUploader()}
        {this.isCustomizationEnabled('home', 'header', 'logoHeightEditor') && this.renderLogoHeightEditor()}
        {this.isCustomizationEnabled('home', 'header', 'faviconUploader') && this.renderFaviconUploader()}
        {this.isCustomizationEnabled('home', 'header', 'backgroundImageUploader') &&
          this.renderBackgroundImageUploader()}
        {this.isCustomizationEnabled('home', 'header', 'darkenBackgroundButtonGroup') &&
          this.renderDarkenBackgroundButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'foregroundImageUploader') &&
          this.renderForegroundImageUploader()}
        {this.isCustomizationEnabled('home', 'header', 'imageAlignButtonGroup') &&
          this.state.academy.foregroundImage &&
          this.renderImageAlignButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'titleColorPicker') && this.renderTitleColorPicker()}
        {this.isCustomizationEnabled('home', 'header', 'descriptionColorPicker') && this.renderDescriptionColorPicker()}
        {this.isCustomizationEnabled('home', 'header', 'textAlignButtonGroup') && this.renderTextAlignButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'subnavAlignButtonGroup') && this.renderSubnavAlignButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'headerLinksEditor') && this.renderHeaderLinksEditor()}
        {isFeatureEnabled('academy_search') &&
          this.isCustomizationEnabled('home', 'header', 'showSearchToggleButtonGroup') &&
          this.renderShowSearchToggleButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'showFiltersToggleButtonGroup') &&
          this.renderShowFiltersToggleButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'backToLinkToggleButtonGroup') &&
          this.renderBackToLinkToggleButtonGroup()}
        {this.isCustomizationEnabled('home', 'header', 'backToLinkEditor') &&
          this.state.academy.themeOptions.showBackToLink &&
          this.renderBackToLinkEditor()}
        {this.isCustomizationEnabled('home', 'header', 'backToLinkTextEditor') &&
          this.state.academy.themeOptions.showBackToLink &&
          this.renderBackToLinkTextEditor()}

        {this.isCustomizationEnabled('home', 'header', 'homePageLinkEditor') && this.renderHomePageLinkEditor()}
        {this.isCustomizationEnabled('home', 'header', 'linkColorPicker') && this.renderLinkColorPicker()}
        {this.isCustomizationEnabled('home', 'header', 'linkHoverColorPicker') && this.renderLinkHoverColorPicker()}
        {this.isCustomizationEnabled('home', 'header', 'imageStyleButtonGroup') &&
          this.state.academy.foregroundImage &&
          this.renderImageStyleButtonGroup()}

        {this.isCustomizationEnabled('home', 'header', 'subnavBackgroundColorPicker') &&
          this.renderSubnavBackgroundColorPicker()}
        {this.isCustomizationEnabled('home', 'header', 'subnavLinkColorPicker') && this.renderSubnavLinkColorPicker()}
        {this.isCustomizationEnabled('home', 'header', 'subnavActiveLinkColorPicker') &&
          this.renderSubnavActiveLinkColorPicker()}
      </SidebarExpandable>
    </AcademyThemingSidebarGroup>
  );

  renderSidebarBodyGroup = () => (
    <AcademyThemingSidebarGroup>
      <AcademySidebarGroupToggle
        onClick={() => this.showSidebarGroup('body')}
        visible={this.state.visibleSidebarGroup === 'body'}
      >
        <Icon name="layout-body:regular" size="lg" />
        Body
      </AcademySidebarGroupToggle>
      <SidebarExpandable
        visible={this.state.visibleSidebarGroup === 'body'}
        numberOfSidebarGroups={this.numberOfSidebarGroups()}
      >
        {this.isCustomizationEnabled('home', 'body', 'googleFontPicker') && this.renderFontPicker()}
        {this.isCustomizationEnabled('home', 'body', 'numberOfColumnsButtonGroup') &&
          this.renderNumberOfColumnsButtonGroup()}
        {this.isCustomizationEnabled('home', 'body', 'cardStyleButtonGroup') && this.renderCardStyleButtonGroup()}
        {this.isCustomizationEnabled('home', 'body', 'collectionCardVersionButtonGroup') &&
          this.renderCollectionCardVersionButtonGroup()}
        {this.isCustomizationEnabled('home', 'body', 'defaultLandingTabDropdown') &&
          this.renderDefaultLandingTabDropdown()}
      </SidebarExpandable>
    </AcademyThemingSidebarGroup>
  );

  renderSidebarFooterGroup = () => (
    <AcademyThemingSidebarGroup>
      <AcademySidebarGroupToggle
        onClick={() => this.showSidebarGroup('footer')}
        visible={this.state.visibleSidebarGroup === 'footer'}
      >
        <Icon name="layout-footer:regular" size="lg" />
        Footer
      </AcademySidebarGroupToggle>

      <SidebarExpandable
        visible={this.state.visibleSidebarGroup === 'footer'}
        numberOfSidebarGroups={this.numberOfSidebarGroups()}
      >
        {this.isCustomizationEnabled('home', 'footer', 'footerBackgroundColorPicker') &&
          this.renderFooterBackgroundColorPicker()}
        {this.isCustomizationEnabled('home', 'footer', 'footerTextEditor') && this.renderFooterTextEditor()}
        {this.isCustomizationEnabled('home', 'footer', 'footerLinksEditor') && this.renderFooterLinksEditor()}
        {this.isCustomizationEnabled('home', 'footer', 'footerTextColorPicker') && this.renderFooterTextColorPicker()}
        {isFeatureEnabled('can_hide_academy_credits') &&
          this.isCustomizationEnabled('home', 'footer', 'showFooterCredits') &&
          this.renderShowFooterCreditsToggleButtonGroup()}
        {this.isCustomizationEnabled('home', 'footer', 'hideFooterLanguagePickerButtonGroup') &&
          this.renderHideFooterLanguagePickerButtonGroup()}
      </SidebarExpandable>
    </AcademyThemingSidebarGroup>
  );

  renderSidebarCustomCode = () => {
    const SIDEBAR_GROUP_KEY = 'custom_code';

    return (
      <AcademyThemingSidebarGroup>
        <AcademySidebarGroupToggle
          onClick={() => this.showSidebarGroup(SIDEBAR_GROUP_KEY)}
          visible={this.state.visibleSidebarGroup === SIDEBAR_GROUP_KEY}
        >
          <Icon name="html:regular" size="lg" />
          Custom Code
        </AcademySidebarGroupToggle>

        <SidebarExpandable
          visible={this.state.visibleSidebarGroup === SIDEBAR_GROUP_KEY}
          numberOfSidebarGroups={this.numberOfSidebarGroups()}
        >
          {this.renderCustomCodeCSSEditor()}
          {this.renderCustomCodeScriptEditor()}
        </SidebarExpandable>
      </AcademyThemingSidebarGroup>
    );
  };

  renderRegistrationSidebarBodyGroup = () => (
    <AcademyThemingSidebarGroup>
      <AcademySidebarGroupToggle
        onClick={() => this.showSidebarGroup('registration-body')}
        visible={this.state.visibleSidebarGroup === 'registration-body'}
      >
        <Icon name="layout-body:regular" size="lg" />
        Body
      </AcademySidebarGroupToggle>

      <SidebarExpandable
        visible={this.state.visibleSidebarGroup === 'registration-body'}
        numberOfSidebarGroups={this.numberOfSidebarGroups()}
      >
        {this.isCustomizationEnabled('registration', 'body', 'registrationBodyBackgroundImageUploader') &&
          this.renderRegistrationBodyBackgroundImageUploader()}
        {this.isCustomizationEnabled('registration', 'body', 'registrationBodyLogoUploader') &&
          this.renderRegistrationBodyLogoUploader()}
        {this.isCustomizationEnabled('registration', 'body', 'termsAndConditionsTextEditor') &&
          this.renderTermsAndConditionsTextEditor(
            'Terms & Conditions (Registration Page)',
            this.state.academy.themeOptions.termsAndConditions,
            'termsAndConditions',
            true,
          )}
        {this.isCustomizationEnabled('registration', 'body', 'termsAndConditionsTextEditor') &&
          this.renderTermsAndConditionsTextEditor(
            'Terms & Conditions (Login Page)',
            this.state.academy.themeOptions.loginTermsAndConditions,
            'loginTermsAndConditions',
          )}
        {this.isCustomizationEnabled('registration', 'body', 'loginRegisterButtonBackgroundColorPicker') &&
          this.renderLoginRegisterButtonBackgroundColorPicker()}
        {this.isCustomizationEnabled('registration', 'body', 'loginRegisterButtonHoverBackgroundColorPicker') &&
          this.renderLoginRegisterButtonHoverBackgroundColorPicker()}
        {this.isCustomizationEnabled('registration', 'body', 'loginRegisterButtonTextColorPicker') &&
          this.renderLoginRegisterButtonTextColorPicker()}
        {this.isCustomizationEnabled('registration', 'body', 'loginRegisterLinkColorPicker') &&
          this.renderLoginRegisterLinkColorPicker()}
      </SidebarExpandable>
    </AcademyThemingSidebarGroup>
  );

  renderRecommendedContentSettings = () =>
    this.props.academy.recommendedContentSectionEnabled &&
    isFeatureEnabled('fy26_q2_clc_recommendations') && (
      <>
        <Divider />
        {this.renderColorPicker(
          'Recommendations Background Color',
          'recommendationsBackgroundColor',
          this.state.academy.themeOptions.recommendationsBackgroundColor || '#F2F4F8',
        )}
        {this.renderColorPicker(
          'Recommendations Title Color',
          'recommendationsTitleColor',
          this.state.academy.themeOptions.recommendationsTitleColor || '#1B1C1C',
        )}
        {this.renderColorPicker(
          'Recommendations Link Color',
          'recommendationsLinkColor',
          this.state.academy.themeOptions.recommendationsLinkColor || '#203A8E',
        )}
        <div className="settings-subsection">
          <div className="my-form settings-section">
            <Select
              label="Arrow Style"
              title="Arrow Style"
              selectedKey={this.state.academy.themeOptions.recommendedationsCarouselArrowStyle || 'default'}
              onSelectionChange={(value) => {
                this.updateThemeOption('recommendedationsCarouselArrowStyle', value);
              }}
            >
              {[
                { label: 'Neutral', value: 'neutral' },
                { label: 'Accent', value: 'accent' },
              ].map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </>
    );

  renderCourseOverviewSidebarBodyGroup = () => (
    <AcademyThemingSidebarGroup>
      <AcademySidebarGroupToggle
        onClick={() => this.showSidebarGroup('course-overview-body')}
        visible={this.state.visibleSidebarGroup === 'course-overview-body'}
      >
        <Icon name="layout-body:regular" size="lg" />
        Body
      </AcademySidebarGroupToggle>

      <SidebarExpandable
        visible={this.state.visibleSidebarGroup === 'course-overview-body'}
        numberOfSidebarGroups={this.numberOfSidebarGroups()}
      >
        {this.isCustomizationEnabled('courseOverview', 'body', 'accentColorPicker') && this.renderAccentColorPicker()}
        {this.isCustomizationEnabled('courseOverview', 'body', 'breadCrumbLinkColorPicker') &&
          this.renderBreadCrumbLinkColorPicker()}
        {this.renderRecommendedContentSettings()}
      </SidebarExpandable>
    </AcademyThemingSidebarGroup>
  );

  renderPreview = () => {
    let iframeSrc = `/admin/academies/${this.state.academy.id}/preview/landing`;
    switch (this.state.theming) {
      case 'register':
        iframeSrc = `/admin/academies/${this.state.academy.id}/preview/register`;
        break;
      case 'courseOverview':
        iframeSrc = `/admin/academies/${this.state.academy.id}/preview/course-overview`;
        break;
    }

    return (
      <AcademyThemingPreview isMobile={this.state.previewDisplay === 'mobile'}>
        {this.state.previewDisplay === 'mobile' && <AcademyAdminiPhoneChrome style={{ top: '25px', left: '35px' }} />}

        {this.state.visibleSidebarGroup === AnnouncementBannerSectionEditorSettings.key ? (
          /* We include the section ID here to allow previewing edits to the announcement bar 
          even when it's not published. For other sections, we skip adding the ID to avoid 
          unnecessary iframe reloads. */
          <AcademyThemeIFrame
            src={`${iframeSrc}?prevent_default=true&admin_theming_section_id=${this.state.visibleSidebarGroup}`}
            key={this.state.previewKey}
          />
        ) : (
          <AcademyThemeIFrame src={`${iframeSrc}?prevent_default=true`} key={this.state.previewKey} />
        )}
        {this.state.previewDisplay === 'mobile' && (
          <AcademyAdminiPhoneTop style={{ top: '43px', left: '56px', zIndex: '15' }} />
        )}
        {this.state.previewDisplay === 'mobile' && (
          <AcademyAdminiPhoneBottom style={{ top: '820px', left: '56px', zIndex: '15' }} />
        )}
      </AcademyThemingPreview>
    );
  };

  renderHomePageLinkEditor = () =>
    this.renderAutoSavingTextField({
      label: 'Homepage Link URL',
      defaultValue: this.state.academy.themeOptions.homepageLink || '',
      saveKey: 'homepageLink',
      tooltip: 'Full URL, including the https://',
    });

  renderHeaderLinksEditor = () => {
    const { customerLearningCloudHub, academy } = this.props;

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <MenuSetting
            label="Header Links"
            externalBaseUrl={academy.academyUrl}
            activeProducts={customerLearningCloudHub.activeProducts}
            onChange={(updatedLinks) =>
              this.saveLinks(
                updatedLinks.map(({ label, href, openInNewTab }) => ({
                  linkText: label,
                  link: href,
                  shouldOpenInNewWindow: openInNewTab,
                })),
                'headerLinks',
              )
            }
            value={this.state.academy.themeOptions.headerLinks.map(({ link, linkText, shouldOpenInNewWindow }) => ({
              label: linkText,
              href: link,
              openInNewTab: shouldOpenInNewWindow,
            }))}
          />
        </div>
      </div>
    );
  };

  renderShowSearchToggleButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: true,
        icons: {
          'fas fa-check': {},
        },
      },
      {
        clickParameter: false,
        icons: {
          'fas fa-times': {},
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'showSearch', 'Show Search');
  };

  renderShowFiltersToggleButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: true,
        icons: {
          'fas fa-check': {},
        },
      },
      {
        clickParameter: false,
        icons: {
          'fas fa-times': {},
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'showFilters', 'Show Filters');
  };

  renderBackToLinkToggleButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: true,
        icons: {
          'fas fa-check': {},
        },
      },
      {
        clickParameter: false,
        icons: {
          'fas fa-times': {},
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'showBackToLink', 'Show Go To Link');
  };

  renderBackToLinkEditor = () =>
    this.renderAutoSavingTextField({
      label: 'Go To Link URL',
      defaultValue: this.state.academy.themeOptions.backToLink || '',
      saveKey: 'backToLink',
      tooltip: 'Full URL, including the https://',
    });

  renderBackToLinkTextEditor = () =>
    this.renderAutoSavingTextField({
      label: 'Go To Link Text',
      defaultValue: this.state.academy.themeOptions.backToLinkText || '',
      saveKey: 'backToLinkText',
    });

  numberOfSidebarGroups = () => {
    let numberOfSidebarGroups = NUMBER_OF_LANDING_SIDEBAR_GROUPS;
    switch (this.state.theming) {
      case 'register':
        numberOfSidebarGroups = NUMBER_OF_REGISTRATION_SIDEBAR_GROUPS;
        break;
      case 'courseOverview':
        numberOfSidebarGroups = NUMBER_OF_COURSE_OVERVIEW_SIDEBAR_GROUPS;
        break;
    }

    return numberOfSidebarGroups;
  };

  saveLinks = (updatedLinks, attribute) => {
    const academyDataToUpdate = {
      theme_options: {
        ...this.state.academy.themeOptions,
        [attribute]: updatedLinks,
      },
    };
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  renderLogoUploader = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <ImageSettingEditor
          name="Logo"
          description="Add a logo to this Academy"
          currentImage={this.state.academy.logo}
          currentImageFileName={this.state.academy.logoFileName}
          handleRemoveClick={(e) => this.handleDeleteImageClick(e, 'logo', 'logo')}
          prepareUploadUrl={`academies/${this.state.academy.id}/prepare_upload/logo`}
          uploadCompletedUrl={`academies/${this.state.academy.id}/upload_completed/logo`}
          uploadCompleted={this.uploadCompleted}
        />
      </div>
    </div>
  );

  renderLogoHeightEditor = () =>
    this.renderAutoSavingTextField({
      label: 'Logo Height',
      defaultValue: this.state.academy.themeOptions.logoHeight || '',
      saveKey: 'logoHeight',
      tooltip:
        'The height, in pixels, of the logo in the navigation. Width will automatically scale to prevent distortion.',
    });

  renderFaviconUploader = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <ImageSettingEditor
          name="Favicon"
          description="Add a favicon to this Academy"
          currentImage={this.state.academy.favicon}
          currentImageFileName={this.state.academy.faviconFileName}
          handleRemoveClick={(e) => this.handleDeleteImageClick(e, 'favicon', 'favicon')}
          prepareUploadUrl={`academies/${this.state.academy.id}/prepare_upload/favicon`}
          uploadCompletedUrl={`academies/${this.state.academy.id}/upload_completed/favicon`}
          uploadCompleted={this.uploadCompleted}
        />
      </div>
    </div>
  );

  renderFontPicker = () => {
    const { googleFontsApiKey, academy } = this.props;
    const {
      academy: {
        themeOptions: { customGoogleFontFamily },
        customFontFile,
      },
      customFont,
    } = this.state;
    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <WrFontPicker
            label="Font"
            customFont={customFont}
            customFontFile={customFontFile}
            googleFontsApiKey={googleFontsApiKey}
            customGoogleFontFamily={customGoogleFontFamily}
            updateFontOptions={this.updateFontOptions}
            academyId={academy.id}
          />
        </div>
      </div>
    );
  };

  renderLinkColorPicker = () => {
    const { academy } = this.state;
    const color = academy.themeOptions.navigationLinkColor || theme.color.textPrimary;

    return this.renderColorPicker('Navigation Link Color', 'navigationLinkColor', color);
  };

  renderLinkHoverColorPicker = () => {
    const { academy } = this.state;
    const color = academy.themeOptions.navigationLinkHoverColor || theme.color.textPrimary;

    return this.renderColorPicker('Navigation Link Hover Color', 'navigationLinkHoverColor', color);
  };

  renderBackgroundImageUploader = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <ImageSettingEditor
          name="Background Image"
          description="Add a custom background image to this Academy"
          currentImage={this.state.academy.backgroundImage}
          currentImageFileName={this.state.academy.backgroundImageFileName}
          handleRemoveClick={(e) => this.handleDeleteImageClick(e, 'background_image', 'background image')}
          prepareUploadUrl={`academies/${this.state.academy.id}/prepare_upload/background_image`}
          uploadCompletedUrl={`academies/${this.state.academy.id}/upload_completed/background_image`}
          uploadCompleted={this.uploadCompleted}
        />
      </div>
    </div>
  );

  renderForegroundImageUploader = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <ImageSettingEditor
          name="Foreground Image"
          description="Add a custom foreground image to this Academy"
          currentImage={this.state.academy.foregroundImage}
          currentImageFileName={this.state.academy.foregroundImageFileName}
          handleRemoveClick={(e) => this.handleDeleteImageClick(e, 'foreground_image', 'foreground image')}
          prepareUploadUrl={`academies/${this.state.academy.id}/prepare_upload/foreground_image`}
          uploadCompletedUrl={`academies/${this.state.academy.id}/upload_completed/foreground_image`}
          uploadCompleted={this.uploadCompleted}
        />
      </div>
    </div>
  );

  renderAutoSavingTextField = ({ label, defaultValue, saveKey, tooltip }) => {
    const handleDebouncedUpdate = _.debounce((value) => {
      this.props.onOpenToast('Saving...');
      this.updateThemeOption(saveKey, value);
    }, 750);

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <TextField label={label} defaultValue={defaultValue} onChange={handleDebouncedUpdate} tooltip={tooltip} />
        </div>
      </div>
    );
  };

  renderAutoSavingTextArea = ({ label, defaultValue, saveKey, tooltip }) => {
    const handleDebouncedUpdate = _.debounce((value) => {
      this.updateThemeOption(saveKey, value);
    }, 1000);

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <TextArea label={label} defaultValue={defaultValue} onChange={handleDebouncedUpdate} tooltip={tooltip} />
        </div>
      </div>
    );
  };

  renderButtonGroup = (buttons, themeOption, groupTitle) => {
    const renderedButtons = buttons.map((button) => {
      const icons = Object.keys(button.icons).map((iconClass, idx) => {
        const iconStyles = button.icons[iconClass];
        return <i key={`icon-${idx}`} className={iconClass} style={iconStyles} />;
      });

      return (
        <Button
          key={`${button.clickParameter}-${themeOption}-${groupTitle}`}
          onClick={() => this.updateThemeOption(themeOption, button.clickParameter)}
          active={this.state.academy.themeOptions[themeOption] === button.clickParameter}
          hoverBackgroundColor={theme.color.darkBlue}
          buttonWidth={100 / buttons.length}
        >
          {icons}
        </Button>
      );
    });

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          {groupTitle}
          <ButtonGroup>{renderedButtons}</ButtonGroup>
        </div>
      </div>
    );
  };

  renderRadioGroup = (buttons, themeOption, groupTitle) => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <RadioGroup
          label={groupTitle}
          value={this.state.academy.themeOptions[themeOption]}
          onChange={(v) => this.updateThemeOption(themeOption, v)}
        >
          <RadioButtonGroup>
            {buttons.map((option) => (
              <RadioButton
                key={option.value}
                value={option.value}
                isBlock
                size="lg"
                {...(option.iconName ? { leadingIconName: option.iconName } : {})}
              >
                {option?.label}
              </RadioButton>
            ))}
          </RadioButtonGroup>
        </RadioGroup>
      </div>
    </div>
  );

  renderImageStyleButtonGroup = () => {
    const buttonsToRender = [
      {
        clickParameter: 'square',
        icons: {
          'fas fa-square': {},
        },
      },
      {
        clickParameter: 'circle',
        icons: {
          'fas fa-circle': {},
        },
      },
      {
        clickParameter: 'diamond',
        icons: {
          'fas fa-square': {
            transform: 'rotate(45deg)',
          },
        },
      },
    ];

    return this.renderButtonGroup(buttonsToRender, 'imageStyle', 'Foreground Image Style');
  };

  renderDarkenBackgroundButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: true,
        icons: {
          'fas fa-check': {},
        },
      },
      {
        clickParameter: false,
        icons: {
          'fas fa-times': {},
        },
      },
    ];
    return this.renderButtonGroup(radioButtons, 'darkenBackgroundImage', 'Darken Background Image');
  };

  renderColorPicker = (pickerTitle, themeOptionsKey, color) => (
    <div className="settings-subsection" key={`${themeOptionsKey}-${color}`}>
      <div className="my-form settings-section">
        <ColorPickerSetting
          isOpen={this.state.displayedColorPicker === themeOptionsKey}
          label={pickerTitle}
          allowAlpha={false}
          value={color}
          onOpenAreaPopover={(val) => {
            this.setState({ displayedColorPicker: val ? themeOptionsKey : null });
          }}
          onChange={(c) => this.handleThemeOptionColorPickerComplete(themeOptionsKey, chroma(c).hex())}
        />
      </div>
    </div>
  );

  renderTitleColorPicker = () => {
    const color = this.state.academy.themeOptions.titleColor || theme.color.textPrimary;

    return this.renderColorPicker('Primary Color', 'titleColor', color);
  };

  renderDescriptionColorPicker = () => {
    const color = this.state.academy.themeOptions.descriptionColor || theme.color.textPrimary;

    return this.renderColorPicker('Secondary Color', 'descriptionColor', color);
  };

  renderTextAlignButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: 'left',
        icons: {
          'fas fa-align-left': { transform: 'scaleY(-1)' },
        },
      },
      {
        clickParameter: 'center',
        icons: {
          'fas fa-align-center': { transform: 'scaleY(-1)' },
        },
      },
      {
        clickParameter: 'right',
        icons: {
          'fas fa-align-right': { transform: 'scaleY(-1)' },
        },
      },
    ];
    return this.renderButtonGroup(radioButtons, 'textAlign', 'Text Alignment');
  };

  renderSubnavAlignButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: 'left',
        icons: {
          'fas fa-align-left': { transform: 'scaleY(-1)' },
        },
      },
      {
        clickParameter: 'center',
        icons: {
          'fas fa-align-center': { transform: 'scaleY(-1)' },
        },
      },
      {
        clickParameter: 'right',
        icons: {
          'fas fa-align-right': { transform: 'scaleY(-1)' },
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'subnavAlign', 'Sub-Navigation Alignment');
  };

  renderImageAlignButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: 'left',
        icons: {
          'fas fa-image': {},
          'fas fa-align-justify': {},
        },
      },
      {
        clickParameter: 'right',
        icons: {
          'fas fa-align-justify': {},
          'fas fa-image': {},
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'imageAlign', 'Image Alignment');
  };

  renderNumberOfColumnsButtonGroup = () => {
    const buttonsToRender = [
      {
        clickParameter: 2,
        icons: {
          'fas fa-align-justify column-1': {},
          'fas fa-align-justify column-2': {},
        },
      },
      {
        clickParameter: 3,
        icons: {
          'fas fa-align-justify column-1': {},
          'fas fa-align-justify column-2': {},
          'fas fa-align-justify column-3': {},
        },
      },
    ];

    return this.renderButtonGroup(buttonsToRender, 'numberOfColumns', 'Number of Columns');
  };

  renderCardStyleButtonGroup = () => {
    const buttonsToRender = [
      {
        clickParameter: 'unbordered',
        icons: {
          'fas fa-square': {},
        },
      },
      {
        clickParameter: 'bordered',
        icons: {
          'far fa-square': {},
        },
      },
    ];

    return this.renderButtonGroup(buttonsToRender, 'cardStyle', 'Card Style');
  };

  renderCollectionCardVersionButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: 1,
        icons: {
          'far fa-square': {},
        },
      },
      {
        clickParameter: 2,
        icons: {
          'fas fa-square': {},
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'collectionCardVersion', 'Collection Card Style');
  };

  labelShouldIncludeKey = (labelOverrideKey) => {
    const {
      academy: { certificationsEnabled, eventsEnabled, pathsEnabled },
    } = this.state;

    const isCertificationKey = APP_CONSTANTS.ACADEMY_LABEL_OVERRIDE_CERTIFICATION_KEYS.includes(labelOverrideKey);
    const isEventKey = APP_CONSTANTS.ACADEMY_LABEL_OVERRIDE_EVENT_KEYS.includes(labelOverrideKey);
    const isPathKey = APP_CONSTANTS.ACADEMY_LABEL_OVERRIDE_PATH_KEYS.includes(labelOverrideKey);
    const isTrainingKey = APP_CONSTANTS.ACADEMY_LABEL_OVERRIDE_TRAINING_KEYS.includes(labelOverrideKey);
    const isMyTrainingKey = APP_CONSTANTS.ACADEMY_LABEL_OVERRIDE_MY_TRAINING_KEYS.includes(labelOverrideKey);

    return (
      (isCertificationKey && certificationsEnabled) ||
      (isPathKey && pathsEnabled && isFeatureEnabled('academy_paths')) ||
      (isEventKey && eventsEnabled) ||
      isTrainingKey ||
      (isMyTrainingKey && isFeatureEnabled('academies_assignments'))
    );
  };

  updateContentLabelOverrides = (option, value) => {
    const { contentLabelOverrides } = this.state.academy.themeOptions;
    contentLabelOverrides[option] = value;

    const academyDataToUpdate = {
      theme_options: {
        ...this.state.academy.themeOptions,
        contentLabelOverrides,
      },
    };
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  renderSubnavBackgroundColorPicker = () => {
    const color = this.state.academy.themeOptions.subnavBackgroundColor;

    return this.renderColorPicker('Subnav Background Color', 'subnavBackgroundColor', color);
  };

  renderSubnavLinkColorPicker = () => {
    const color = this.state.academy.themeOptions.subnavLinkColor;

    return this.renderColorPicker('Subnav Link Color', 'subnavLinkColor', color);
  };

  renderSubnavActiveLinkColorPicker = () => {
    const color = this.state.academy.themeOptions.subnavActiveLinkColor;

    return this.renderColorPicker('Subnav Active Link Color', 'subnavActiveLinkColor', color);
  };

  renderDefaultLandingTabDropdown = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <DefaultLandingPageSelect
          label="Default Landing Page"
          academyId={this.state.academy.id}
          defaultLandingPage={this.state.academy.defaultLandingTab}
          onChange={this.handleDefaultLandingTabSelect}
          themeOptions={this.state.academy.themeOptions}
          eventsEnabled={this.state.academy.eventsEnabled}
          collectionsEnabled={this.state.academy.collectionsEnabled}
          certificationsEnabled={this.state.academy.certificationsEnabled}
          pathsEnabled={this.state.academy.pathsEnabled}
          pagesOrder={this.state.academy.pagesOrder}
        />
      </div>
    </div>
  );

  renderFooterTextEditor = () =>
    this.renderAutoSavingTextArea({
      label: 'Text',
      defaultValue: this.state.academy.themeOptions.footerText || '',
      saveKey: 'footerText',
    });

  renderFooterLinksEditor = () => {
    const { academy, customerLearningCloudHub } = this.props;

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <MenuSetting
            label="Footer Links"
            externalBaseUrl={academy.academyUrl}
            activeProducts={customerLearningCloudHub.activeProducts}
            onChange={(updatedLinks) =>
              this.saveLinks(
                updatedLinks.map(({ label, href, openInNewTab }) => ({
                  linkText: label,
                  link: href,
                  shouldOpenInNewWindow: openInNewTab,
                })),
                'footerLinks',
              )
            }
            value={this.state.academy.themeOptions.footerLinks.map(({ link, linkText, shouldOpenInNewWindow }) => ({
              label: linkText,
              href: link,
              openInNewTab: shouldOpenInNewWindow,
            }))}
          />
        </div>
      </div>
    );
  };

  renderFooterBackgroundColorPicker = () => {
    const color = this.state.academy.themeOptions.footerBackgroundColor || theme.color.darkBlue;

    return this.renderColorPicker('Background Color', 'footerBackgroundColor', color);
  };

  renderFooterTextColorPicker = () => {
    const color = this.state.academy.themeOptions.footerTextColor || '#fff';

    return this.renderColorPicker('Text Color', 'footerTextColor', color);
  };

  renderCustomCodeCSSEditor = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <TextArea
          label="Custom CSS Rules"
          defaultValue={this.state.academy.themeOptions.customCSSRules || ''}
          onBlur={(e) => this.updateThemeOption('customCSSRules', e.target.value)}
          placeholder={`/* css rules go here */
          .AL_Custom_CardTitle {
            ...
          }`}
          rows={10}
        />
        <Link
          href="http://help.workramp.com/en/articles/6645543-adding-css-javascript-to-your-academy"
          target="_blank"
          rel="noopener noreferrer"
          size={1}
        >
          Documentation
        </Link>
      </div>
    </div>
  );

  renderCustomCodeScriptEditor = () => {
    const { academy } = this.state;

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <TextArea
            label="Custom Script Tags"
            defaultValue={this.state.academy.themeOptions.customScriptTags || ''}
            onBlur={(e) => this.updateThemeOption('customScriptTags', e.target.value)}
            placeholder={
              academy.hosting === APP_CONSTANTS.HOSTING_TYPES.CUSTOM_SUBDOMAIN
                ? 'Custom Domain must be set up in Settings > General before custom scripts can be added'
                : ''
            }
            rows={10}
            disabled={academy.hosting === APP_CONSTANTS.HOSTING_TYPES.CUSTOM_SUBDOMAIN}
          />
        </div>
      </div>
    );
  };

  renderShowFooterCreditsToggleButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: true,
        icons: {
          'fas fa-check': {},
        },
      },
      {
        clickParameter: false,
        icons: {
          'fas fa-times': {},
        },
      },
    ];
    return this.renderButtonGroup(radioButtons, 'showFooterCredits', 'Show WorkRamp Logo');
  };

  renderHideFooterLanguagePickerButtonGroup = () => {
    const radioButtons = [
      {
        clickParameter: true,
        icons: {
          'fas fa-check': {},
        },
      },
      {
        clickParameter: false,
        icons: {
          'fas fa-times': {},
        },
      },
    ];

    return this.renderButtonGroup(radioButtons, 'hideFooterLanguagePicker', 'Hide Language Picker');
  };

  renderRegistrationBodyBackgroundImageUploader = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <ImageSettingEditor
          name="Background Image"
          description="Add a custom background image to this registration page"
          currentImage={this.state.academy.registrationBackgroundImage}
          currentImageFileName={this.state.academy.registrationBackgroundImageFileName}
          handleRemoveClick={(e) =>
            this.handleDeleteImageClick(e, 'registration_background_image', 'registration background image')
          }
          prepareUploadUrl={`academies/${this.state.academy.id}/prepare_upload/registration_background_image`}
          uploadCompletedUrl={`academies/${this.state.academy.id}/upload_completed/registration_background_image`}
          uploadCompleted={this.uploadCompleted}
        />
      </div>
    </div>
  );

  renderRegistrationBodyLogoUploader = () => (
    <div className="settings-subsection">
      <div className="my-form settings-section">
        <ImageSettingEditor
          name="Logo"
          description="Add a custom logo to this registration page"
          currentImage={this.state.academy.registrationLogo}
          currentImageFileName={this.state.academy.registrationLogoFileName}
          handleRemoveClick={(e) => this.handleDeleteImageClick(e, 'registration_logo', 'registration logo')}
          prepareUploadUrl={`academies/${this.state.academy.id}/prepare_upload/registration_logo`}
          uploadCompletedUrl={`academies/${this.state.academy.id}/upload_completed/registration_logo`}
          uploadCompleted={this.uploadCompleted}
        />
      </div>
    </div>
  );

  renderTermsAndConditionsTextEditor = (sectionTitle, initialText, themeFieldToUpdate, showRequiredSetting) => {
    const { requireExplicitTermsAndConditionsAgreement } = this.state.academy.themeOptions;

    return (
      <div className="settings-subsection">
        <div className="my-form settings-section">
          <div className="form-label">{sectionTitle}</div>
          <TextEditor
            showBorder
            mode="ACADEMY_REGISTRATION_PAGE_EDITOR"
            value={this.state[themeFieldToUpdate]}
            onChange={(html) => {
              this.setState({ [themeFieldToUpdate]: html });
              this.updateThemeOption(themeFieldToUpdate, html);
            }}
          />
        </div>
        {showRequiredSetting && (
          <TCAgreementRequiredContainer>
            <AgreementWrapper>
              <span>Agreement Required</span>
              <StyledReactTooltipInfoMarker
                html
                text="Require registrants to provide explicit agreement<br />(if option is selected, change text above)"
              />
            </AgreementWrapper>
            <Toggle
              theme="v2"
              onChange={() =>
                this.updateThemeOption(
                  'requireExplicitTermsAndConditionsAgreement',
                  !requireExplicitTermsAndConditionsAgreement,
                )
              }
              active={requireExplicitTermsAndConditionsAgreement}
            />
          </TCAgreementRequiredContainer>
        )}
      </div>
    );
  };

  renderLoginRegisterButtonBackgroundColorPicker = () => {
    const color = this.state.academy.themeOptions.loginRegisterButtonBackgroundColor;

    return this.renderColorPicker('Button Background Color', 'loginRegisterButtonBackgroundColor', color);
  };

  renderLoginRegisterButtonHoverBackgroundColorPicker = () => {
    const color = this.state.academy.themeOptions.loginRegisterButtonHoverBackgroundColor;

    return this.renderColorPicker('Button Background Hover Color', 'loginRegisterButtonHoverBackgroundColor', color);
  };

  renderLoginRegisterButtonTextColorPicker = () => {
    const color = this.state.academy.themeOptions.loginRegisterButtonTextColor;

    return this.renderColorPicker('Button Text Color', 'loginRegisterButtonTextColor', color);
  };

  renderLoginRegisterLinkColorPicker = () => {
    const color = this.state.academy.themeOptions.loginRegisterLinkColor;

    return this.renderColorPicker('Link Color', 'loginRegisterLinkColor', color);
  };

  renderAccentColorPicker = () => {
    const color = this.state.academy.themeOptions.accentColor;

    return this.renderColorPicker('Accent Color', 'accentColor', color);
  };

  renderBreadCrumbLinkColorPicker = () => {
    const color = this.state.academy.themeOptions.breadCrumbLinkColor;

    return this.renderColorPicker('Breadcrumb Link Color', 'breadCrumbLinkColor', color);
  };

  handleDeleteImageClick = (e, asset, confirmAssetName) => {
    e.preventDefault();

    WRModals.confirm(`Are you sure you want to delete this ${confirmAssetName}?`, () => {
      const academyDataToUpdate = {};
      academyDataToUpdate[asset] = '';
      this.saveAcademyAndUpdateState(academyDataToUpdate);
    });
  };

  updateFontOptions = (googleValue, customValue) => {
    const {
      academy: { themeOptions },
    } = this.state;
    const academyDataToUpdate = {
      theme_options: {
        ...themeOptions,
        [CUSTOM_GOOGLE_FONT_FAMILY]: googleValue,
        [CUSTOM_FONT]: customValue,
      },
    };
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  updateThemeOption = (option, value) => {
    if (!option) {
      return;
    }
    const academyDataToUpdate = {
      theme_options: {
        ...this.state.academy.themeOptions,
        [option]: value,
      },
    };
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  updateThemeTabTitle = (option, value) => {
    if (!option) {
      return;
    }
    const academyDataToUpdate = {
      theme_options: {
        ...this.state.academy.themeOptions,
        tabTitles: {
          ...(this.state.academy.themeOptions.tabTitles || {}),
          [option]: value,
        },
      },
    };
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  handleThemeOptionColorPickerComplete = (themeOption, color) => {
    const academyDataToUpdate = {
      theme_options: {
        ...this.state.academy.themeOptions,
      },
    };
    academyDataToUpdate.theme_options[themeOption] = color;
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  handleDefaultLandingTabSelect = (newTab) => {
    const academyDataToUpdate = {
      default_landing_tab: newTab,
    };
    this.saveAcademyAndUpdateState(academyDataToUpdate);
  };

  saveAcademyAndUpdateState = (academyDataToUpdate) => {
    AcademiesApi.update(this.state.academy.id, academyDataToUpdate).then((updatedAcademy) => {
      this.props.onOpenToast('Saved');
      this.setState({
        academy: updatedAcademy,
      });
    });
  };

  updateAcademyStateValue = (attribute, value) => {
    this.setState({
      academy: {
        ...this.state.academy,
        [attribute]: value,
      },
    });
  };

  uploadCompleted = (updatedAcademy) => {
    this.setState({
      academy: updatedAcademy,
    });
  };

  incrementPreviewKey = () => {
    this.setState({
      previewKey: this.state.previewKey + 1,
    });
  };
}

export const AcademyAdminTheming = (props) => {
  const { open: openToast } = useToast();

  const handleOpenToast = (msg) => {
    openToast({ children: msg, autoDismissDuration: 2000 });
  };

  return <BaseAcademyAdminTheming onOpenToast={handleOpenToast} {...props} />;
};

const PageWrapper = styled.div`
  padding: 0 !important;
  display: flex;
  overflow: hidden;
`;

const AcademyThemingSidebar = styled.div`
  width: ${SIDEBAR_WIDTH}px;
  ${(props) => props.previewExpanded && 'width: 0;'}
  height: calc(100vh - ${ADMIN_HEIGHT}px);
  box-shadow: 5px 0 15px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  position: relative;
  transition: all 0.3s ease-in-out;
  .text-editor-status {
    position: absolute;
    top: 100%;
    right: 0;
    font-size: 12px;
  }
  .settings-editor-outer {
    padding: 0;
    min-height: auto;
    background: none;
    transition: all 0.3s ease-in-out;
    ${(props) => props.previewExpanded && 'opacity: 0;'}
    .settings-page-title {
      padding-top: 20px 30px;
      background-color: #f0f3f7;
      color: #007aff;
      border: none;
    }
    .settings-editor {
      box-shadow: none;
      border-radius: 0;
      background: none;
      .settings-section {
        flex: none;
        background: none;
      }
      .file-uploader-link {
        font-size: 12px;
      }
    }
    .settings-subsection {
      padding: 20px 30px 30px 30px;
      display: block;
      color: #8998ac;
    }
    .settings-subsection-multi {
      .form-field {
        margin-bottom: 0;
        .form-label {
          font-size: 14px;
          margin-bottom: 0;
          margin-top: 8px;
        }
      }
    }
    .form-field {
      .form-label {
        font-size: 16px;
        color: #8998ac;
      }
      .form-input-text {
        border: solid 1px #ececec;
        background-color: #f7f7f7;
        font-size: 12px;
        color: #8998ac !important;
        padding: 7px;
        border-radius: 4px;
      }
    }
    .upload-with-progress {
      font-size: 12px;
    }
    .sortable-icon {
      cursor: pointer;
      margin: 7px 3px 0 7px;
      display: inline-block;
    }
  }
`;

const ThemingRight = styled.div`
  width: calc(100% - ${SIDEBAR_WIDTH}px);
  ${(props) => props.previewExpanded && 'width: 100%;'}
  transition: all .3s ease-in-out;
  height: calc(100vh - ${ADMIN_HEIGHT}px);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 9;
`;

const AcademyThemingPreview = styled.div`
  width: 100%;
  transition: all 0.3s ease-in-out;
  height: calc(100vh - ${ADMIN_HEIGHT + THEME_TOGGLE_HEIGHT}px);
  background: url(/images/loader2.gif) center center no-repeat #fff;
  z-index: 5;
  position: relative;
  svg {
    opacity: 0;
    position: absolute;
    transition: all 0.3s ease-in-out;
  }
  ${(props) =>
    props.isMobile &&
    `
    background: none;
    height: auto;
    padding: 25px 0 50px 35px;
    iframe {
      width: ${MOBILE_CONTENT_WIDTH}px !important;
      height: ${MOBILE_CONTENT_HEIGHT}px !important;
      margin-top: ${(MOBILE_HEIGHT - MOBILE_CONTENT_HEIGHT) / 2}px;
      margin-left: ${(MOBILE_WIDTH - MOBILE_CONTENT_WIDTH) / 2}px;
      border-radius: 40px;
      position: relative;
      z-index: 10;
    }
    svg {
      opacity: 1;
    }
  `}
`;

const ButtonGroup = styled.div`
  border-radius: 3px;
  box-sizing: border-box;
  margin-top: 10px;
`;

const Button = styled.div`
  display: inline-block;
  width: ${(props) => props.buttonWidth}%;
  text-align: center;
  padding: 10px 5px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  background-color: #f7f7f7;
  &:hover {
    background-color: ${(props) => props.hoverBackgroundColor};
    color: #fff;
  }
  ${(props) => props.active && `background-color: ${props.hoverBackgroundColor}; color: #fff;`}
  &:first-child {
    border-radius: 3px 0 0 3px;
  }
  &:last-child {
    border-radius: 0 3px 3px 0;
  }
  i {
    margin: 0 3px;
  }
`;

const AcademyThemingSidebarGroup = styled.div`
  background-color: #fff;
`;

const SidebarExpandable = styled.div`
  box-sizing: border-box;
  overflow: auto;
  height: 0;
  opacity: 0;
  transition: height 0.3s ease-in-out;
  &::-webkit-scrollbar {
    display: none;
  }
  ${(props) =>
    props.visible && `height: calc(100vh - ${SIDEBAR_TOGGLE_HEIGHT * props.numberOfSidebarGroups + ADMIN_HEIGHT}px);`}
  ${(props) => props.visible && 'opacity: 1;'}

  div[id^=font-picker] {
    background-color: #f7f7f7;
    width: calc(100% - 2px);
    border-radius: 4px;
    border: solid 1px #ececec;
    box-shadow: none;
    margin-top: 5px;
    &.expanded {
      border-radius: 4px 4px 0 0;
      ul {
        border: solid 1px #ececec;
      }
    }
    .dropdown-button {
      background: none !important;
    }
    .dropdown-icon {
      &.finished {
        &:before {
          border-top-color: #d8d8d8;
        }
      }
    }
    ul {
      background-color: #f7f7f7;
      border-top: none;
      border-radius: 0 0 4px 4px;
      box-shadow: none;
      width: calc(100% + 2px);
      left: -1px;
      li {
        button {
          &.active-font,
          &:hover {
            background-color: #ececec;
          }
        }
      }
    }
  }
`;

const AcademySidebarGroupToggle = styled.h2`
  cursor: pointer;
  margin: 0;
  padding: 0 30px;
  background-color: #f5f8fc;
  border-bottom: solid 1px #e8e8e8;
  color: #3b4859;
  text-transform: uppercase;
  height: ${SIDEBAR_TOGGLE_HEIGHT}px;
  line-height: ${SIDEBAR_TOGGLE_HEIGHT}px;
  box-sizing: border-box;
  font-size: 16px;
  position: relative;
  display: flex;
  align-items: center;
  column-gap: 24px;
  ${theme.font.semibold}
  &:after {
    content: '▾';
    display: block;
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%) rotate(-90deg);
    color: #d8d8d8;
    transition: all 0.3s ease-in-out;
  }
  ${(props) =>
    props.visible &&
    `
    &:after {
      transform: translateY(-50%);
    }
  `}
  img {
    height: 20px;
    width: 20px;
    display: inline-block;
  }
`;

const SidebarUtilitiesWrapper = styled.div`
  > div {
    margin-top: 0;
    > div {
      height: ${THEME_TOGGLE_HEIGHT}px;
      background: none !important;
      color: inherit !important;
      opacity: 0.35;
      &.active {
        opacity: 1;
      }
      i {
        margin-top: 2px;
      }
      &:first-child {
        border-radius: 0;
        .fa-caret-right {
          display: none;
        }
        ${(props) => props.previewExpanded && '.fa-caret-left { display: none; }'}
        ${(props) => props.previewExpanded && '.fa-caret-right { display: inline-block; }'}
      }
      &:last-child {
        border-radius: 0;
      }
    }
  }
`;

const AcademyThemeIFrame = styled.iframe`
  border: none;
  width: 100%;
  height: 100%;
`;

const AcademyThemeingChoices = styled.div`
  width: 100%;
  transition: all 0.3s ease-in-out;
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  height: ${THEME_TOGGLE_HEIGHT}px;
  background-color: #eff2f7;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  z-index: 10;
  a {
    display: inline-block;
    padding: 0 30px;
    color: #3b4859;
    transition: all 0.3s ease-in-out;
    height: ${THEME_TOGGLE_HEIGHT}px;
    line-height: ${THEME_TOGGLE_HEIGHT}px;
    font-size: 15px;
    box-sizing: border-box;
    border-bottom: solid 4px transparent;
    &:hover,
    &.active {
      text-decoration: none;
      border-bottom-color: #007aff;
    }
  }
`;

const StyledReactTooltipInfoMarker = styled(ReactTooltipInfoMarker)`
  margin-left: 8px;
`;

const TCAgreementRequiredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AgreementWrapper = styled.div`
  display: flex;
  align-items: center;
`;
